import { BranchesOutlined, CheckOutlined, CloseOutlined, DownOutlined, GlobalOutlined, ShoppingCartOutlined } from "@ant-design/icons";
import { Spin, Tree } from "antd";
import type { EventDataNode } from "antd/es/tree";
import { Palette } from "core/styles/palette";
import i18n from "i18next";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router";

import {
    useLazyOrganisationUnitsTreeLoadChildrenQuery,
    useLazyOrganisationUnitsTreeLoadQuery,
} from "src/app/features/OrganisationUnitsTree/services/server/OrganisationUnitsTreeApi";
import type { SiteGroupTreeNode, TreeStructure } from "src/app/features/OrganisationUnitsTree/types/OrganisationUnitsTreeDto";
import { ResourceName } from "../../../../../core/router/config/ResourceName";
import { BASE_PATH, FORM_ACTION } from "../../../../../core/router/types/routerTypes";

const ROOT_KEY = "0-0";

/** Convert backend response nodes to tree data (children are loaded lazily) */
const convertDataToTreeData = (data: SiteGroupTreeNode[]): TreeStructure[] => {
    return data.map((node) => {
        if (node.terminal) {
            return {
                title: `${node.name ?? ""} ${node.terminalId ?? ""} (${node.id})`,
                key: `terminal-${node.id}`,
                id: node.id,
                unitType: ResourceName.TERMINALS,
                isLeaf: true,
                icon:
                    node.status === "ACTIVE" ? (
                        <CheckOutlined style={{ color: Palette.HeroGreen }} />
                    ) : (
                        <CloseOutlined style={{ color: Palette.AeviRed }} />
                    ),
            };
        }

        const isSiteGroup = node.unitType === "SITE_GROUP";

        return {
            title: `${node.name} (${node.id})`,
            key: `node-${node.name}-${node.id}`,
            id: node.id,
            unitType: isSiteGroup ? ResourceName.SITE_GROUPS : ResourceName.SITES,
            icon: isSiteGroup ? <BranchesOutlined /> : <ShoppingCartOutlined />,
            isLeaf: !node.hasChildren,
        };
    });
};

/** Recursively update the tree data, setting children for the node matching `key` */
const updateTreeData = (treeData: TreeStructure[], key: string, children: TreeStructure[]): TreeStructure[] =>
    treeData.map((node) => {
        if (node.key === key) {
            return { ...node, children };
        }
        if (node.children) {
            return { ...node, children: updateTreeData(node.children, key, children) };
        }
        return node;
    });

export const OrganisationUnitsTree = () => {
    const navigate = useNavigate();

    const [treeData, setTreeData] = useState<TreeStructure[]>([
        {
            title: i18n.t("menu.organisationUnitsTree"),
            key: ROOT_KEY,
            icon: <GlobalOutlined />,
            isLeaf: false,
        },
    ]);

    const [loading, setLoading] = useState(false);

    const [fetchRootChildren] = useLazyOrganisationUnitsTreeLoadQuery();
    const [fetchNodeChildren] = useLazyOrganisationUnitsTreeLoadChildrenQuery();

    const onLoadData = useCallback(
        async (node: EventDataNode<TreeStructure>) => {
            // Already loaded — skip
            if (node.children) {
                return;
            }

            setLoading(true);
            try {
                let children: TreeStructure[] = [];

                if (node.key === ROOT_KEY) {
                    // Expand root → fetch top-level site groups
                    const result = await fetchRootChildren().unwrap();
                    children = convertDataToTreeData(result.siteGroupTreeNodes ?? []);
                } else if (node.id != null) {
                    // Expand a SiteGroup or Site → fetch its children
                    const result = await fetchNodeChildren({ parentId: node.id }).unwrap();
                    children = convertDataToTreeData(result.siteGroupTreeNodes ?? []);
                }

                if (children.length > 0) {
                    setTreeData((prev) => updateTreeData(prev, node.key, children));
                }
            } finally {
                setLoading(false);
            }
        },
        [fetchRootChildren, fetchNodeChildren],
    );

    const onRightClick = (node: EventDataNode<TreeStructure>) => {
        if (node.unitType === ResourceName.TERMINALS && node.id != null) {
            navigate(`${BASE_PATH}/${ResourceName.TERMINALS}/${FORM_ACTION.EDIT}/${node.id}`);
        }
    };

    return (
        <Spin spinning={loading}>
            <Tree
                showIcon
                defaultSelectedKeys={[ROOT_KEY]}
                switcherIcon={<DownOutlined />}
                treeData={treeData}
                loadData={onLoadData}
                onRightClick={(item) => onRightClick(item.node)}
                onDoubleClick={(_e, node) => onRightClick(node)}
            />
        </Spin>
    );
};
